import html2pdf from 'html2pdf.js';
import { formatCurrency, MONTH_NAMES, MODEL_TYPE_LABELS } from './financial-calculations';
import type { FinancialModelRecord } from '@/types/financial-model';

// ---- CSV Export ----

export function exportFinancialModelToCsv(model: FinancialModelRecord): void {
  const { months, summary } = model.calculated_data;
  const isSaas = model.model_type === 'saas';

  const headers = ['Показатель', ...MONTH_NAMES, 'Итого'];

  const rows: (string | number)[][] = [
    ['Выручка', ...months.map(m => Math.round(m.revenue)), Math.round(summary.totalRevenue)],
    ['Новые клиенты', ...months.map(m => Math.round(m.newCustomers)), months.reduce((s, m) => s + m.newCustomers, 0).toFixed(0)],
    ['Всего клиентов', ...months.map(m => Math.round(m.totalCustomers)), ''],
  ];

  if (isSaas) {
    rows.push(['Отток', ...months.map(m => Math.round(m.churnedCustomers)), '']);
  }

  rows.push(
    ['Реклама', ...months.map(m => Math.round(m.advertisingCost)), months.reduce((s, m) => s + m.advertisingCost, 0).toFixed(0)],
    ['Переменные расходы', ...months.map(m => Math.round(m.variableCosts)), months.reduce((s, m) => s + m.variableCosts, 0).toFixed(0)],
    ['Инфраструктура', ...months.map(m => Math.round(m.infrastructureCost)), months.reduce((s, m) => s + m.infrastructureCost, 0).toFixed(0)],
    ['Комиссия платежей', ...months.map(m => Math.round(m.paymentProcessingCost)), months.reduce((s, m) => s + m.paymentProcessingCost, 0).toFixed(0)],
    ['Итого расходы', ...months.map(m => Math.round(m.totalExpenses)), Math.round(summary.totalExpenses)],
    ['EBITDA', ...months.map(m => Math.round(m.ebitda)), ''],
    ['Налоги', ...months.map(m => Math.round(m.taxes)), ''],
    ['Чистая прибыль', ...months.map(m => Math.round(m.netProfit)), Math.round(summary.totalNetProfit)],
    ['Баланс', ...months.map(m => Math.round(m.cashBalance)), Math.round(summary.finalCashBalance)],
    ['CAC', ...months.map(m => m.cac !== null ? Math.round(m.cac) : ''), summary.avgCac !== null ? Math.round(summary.avgCac) : ''],
    ['ARPC', ...months.map(m => m.arpc !== null ? Math.round(m.arpc) : ''), ''],
  );

  if (isSaas) {
    rows.push(
      ['LTV', ...months.map(m => m.ltv !== null ? Math.round(m.ltv) : ''), summary.avgLtv !== null ? Math.round(summary.avgLtv) : ''],
      ['LTV/CAC', ...months.map(m => m.ltvCacRatio !== null ? m.ltvCacRatio.toFixed(1) : ''), summary.avgLtvCacRatio !== null ? summary.avgLtvCacRatio.toFixed(1) : ''],
    );
  }

  // Build CSV string
  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.join(';')),
  ].join('\n');

  // BOM for correct Russian encoding in Excel
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${model.model_name} — финмодель.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- PDF Export ----

const EN_MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const EN_MODEL_TYPE_LABELS: Record<string, string> = {
  saas: 'SaaS / Subscription',
  one_time: 'One-time Sales',
  consulting: 'Consulting / Services',
};

export async function exportFinancialModelToPdf(model: FinancialModelRecord, lang = 'ru'): Promise<void> {
  const isEn = lang === 'en';
  const currencySymbol = isEn ? '$' : '₽';
  const exportMonthNames = isEn ? EN_MONTH_NAMES : MONTH_NAMES;
  const exportModelTypeLabels = isEn ? EN_MODEL_TYPE_LABELS : MODEL_TYPE_LABELS;
  const { months, summary } = model.calculated_data;
  const isSaas = model.model_type === 'saas';

  // Build HTML for PDF
  const style = document.createElement('style');
  style.textContent = `
    .fm-pdf * { margin: 0; padding: 0; box-sizing: border-box; }
    .fm-pdf { font-family: 'Segoe UI', sans-serif; color: #0d0d0d; font-size: 11px; }
    .fm-pdf h1 { font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 700; margin-bottom: 4px; }
    .fm-pdf .subtitle { color: #565858; font-size: 13px; margin-bottom: 16px; }
    .fm-pdf .summary-grid { display: flex; gap: 12px; margin-bottom: 20px; }
    .fm-pdf .summary-card { flex: 1; background: #f8f8f8; border-radius: 8px; padding: 12px; }
    .fm-pdf .summary-card .label { color: #565858; font-size: 10px; margin-bottom: 2px; }
    .fm-pdf .summary-card .value { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 16px; }
    .fm-pdf table { width: 100%; border-collapse: collapse; font-size: 9px; }
    .fm-pdf th { background: #f8f8f8; font-weight: 600; text-align: center; padding: 4px 6px; border: 1px solid #e0e0e0; }
    .fm-pdf td { text-align: right; padding: 3px 6px; border: 1px solid #e0e0e0; }
    .fm-pdf td:first-child { text-align: left; font-weight: 500; white-space: nowrap; }
    .fm-pdf .section-row td { background: #f8f8f8; font-weight: 600; text-transform: uppercase; font-size: 8px; letter-spacing: 0.5px; color: #565858; }
    .fm-pdf .negative { color: #e74c3c; }
    .fm-pdf .positive { color: #27ae60; }
    .fm-pdf .ai-section { margin-top: 20px; page-break-before: always; }
    .fm-pdf .ai-section h2 { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 600; margin-bottom: 8px; }
    .fm-pdf .ai-content { line-height: 1.5; font-size: 11px; }
    .fm-pdf .ai-content h2, .fm-pdf .ai-content h3 { font-size: 13px; font-weight: 600; margin: 12px 0 6px; }
    .fm-pdf .ai-content ul, .fm-pdf .ai-content ol { padding-left: 16px; margin: 4px 0; }
  `;
  document.head.appendChild(style);

  const container = document.createElement('div');
  container.className = 'fm-pdf';
  container.style.width = '210mm';
  container.style.padding = '15mm';

  const formatVal = (v: number) => formatCurrency(v);
  const colorClass = (v: number) => v < 0 ? 'negative' : v > 0 ? 'positive' : '';

  // Build metric rows
  type MetricRow = { label: string; values: (string | number)[]; total: string; show: boolean };

  const metricRows: (MetricRow | { section: string })[] = [
    { section: 'Выручка' },
    { label: 'Выручка', values: months.map(m => formatVal(m.revenue)), total: formatVal(summary.totalRevenue), show: true },
    { section: 'Клиенты' },
    { label: 'Новые клиенты', values: months.map(m => Math.round(m.newCustomers).toString()), total: '', show: true },
    { label: 'Всего клиентов', values: months.map(m => Math.round(m.totalCustomers).toString()), total: '', show: true },
    { label: 'Отток', values: months.map(m => Math.round(m.churnedCustomers).toString()), total: '', show: isSaas },
    { section: 'Расходы' },
    { label: 'Реклама', values: months.map(m => formatVal(m.advertisingCost)), total: formatVal(months.reduce((s, m) => s + m.advertisingCost, 0)), show: true },
    { label: 'Переменные', values: months.map(m => formatVal(m.variableCosts)), total: formatVal(months.reduce((s, m) => s + m.variableCosts, 0)), show: true },
    { label: 'Инфраструктура', values: months.map(m => formatVal(m.infrastructureCost)), total: formatVal(months.reduce((s, m) => s + m.infrastructureCost, 0)), show: true },
    { label: 'Комиссия', values: months.map(m => formatVal(m.paymentProcessingCost)), total: formatVal(months.reduce((s, m) => s + m.paymentProcessingCost, 0)), show: true },
    { label: 'Итого расходы', values: months.map(m => formatVal(m.totalExpenses)), total: formatVal(summary.totalExpenses), show: true },
    { section: 'Прибыль' },
    { label: 'EBITDA', values: months.map(m => formatVal(m.ebitda)), total: '', show: true },
    { label: 'Налоги', values: months.map(m => formatVal(m.taxes)), total: '', show: true },
    { label: 'Чистая прибыль', values: months.map(m => formatVal(m.netProfit)), total: formatVal(summary.totalNetProfit), show: true },
    { label: 'Баланс', values: months.map(m => formatVal(m.cashBalance)), total: formatVal(summary.finalCashBalance), show: true },
    { section: 'Юнит-экономика' },
    { label: 'CAC', values: months.map(m => m.cac !== null ? formatVal(m.cac) : '—'), total: summary.avgCac !== null ? formatVal(summary.avgCac) : '—', show: true },
    { label: 'ARPC', values: months.map(m => m.arpc !== null ? formatVal(m.arpc) : '—'), total: '', show: true },
    { label: 'LTV', values: months.map(m => m.ltv !== null ? formatVal(m.ltv) : '—'), total: summary.avgLtv !== null ? formatVal(summary.avgLtv) : '—', show: isSaas },
    { label: 'LTV/CAC', values: months.map(m => m.ltvCacRatio !== null ? m.ltvCacRatio.toFixed(1) : '—'), total: summary.avgLtvCacRatio !== null ? summary.avgLtvCacRatio.toFixed(1) : '—', show: isSaas },
  ];

  const tableRows = metricRows
    .filter(r => 'section' in r || (r as MetricRow).show)
    .map(r => {
      if ('section' in r) {
        return `<tr class="section-row"><td colspan="14">${r.section}</td></tr>`;
      }
      const mr = r as MetricRow;
      return `<tr><td>${mr.label}</td>${mr.values.map(v => `<td>${v}</td>`).join('')}<td style="font-weight:600;background:#f8f8f8">${mr.total}</td></tr>`;
    })
    .join('');

  container.innerHTML = `
    <h1>${model.model_name}</h1>
    <p class="subtitle">${exportModelTypeLabels[model.model_type] ?? model.model_type} | ${isEn ? 'Created' : 'Создано'}: ${new Date(model.created).toLocaleDateString(isEn ? 'en-US' : 'ru-RU')}</p>

    <div class="summary-grid">
      <div class="summary-card">
        <div class="label">${isEn ? 'Total revenue' : 'Общая выручка'}</div>
        <div class="value">${formatVal(summary.totalRevenue)} ${currencySymbol}</div>
      </div>
      <div class="summary-card">
        <div class="label">${isEn ? 'Net profit' : 'Чистая прибыль'}</div>
        <div class="value ${colorClass(summary.totalNetProfit)}">${formatVal(summary.totalNetProfit)} ${currencySymbol}</div>
      </div>
      <div class="summary-card">
        <div class="label">${isEn ? 'Break-even' : 'Окупаемость'}</div>
        <div class="value">${summary.breakEvenMonth ? `${summary.breakEvenMonth} ${isEn ? 'mo.' : 'мес.'}` : (isEn ? 'None' : 'Нет')}</div>
      </div>
      <div class="summary-card">
        <div class="label">ROI</div>
        <div class="value ${colorClass(summary.roi)}">${summary.roi.toFixed(1)}%</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="text-align:left">${isEn ? 'Metric' : 'Показатель'}</th>
          ${exportMonthNames.map(m => `<th>${m}</th>`).join('')}
          <th>${isEn ? 'Total' : 'Итого'}</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>

    ${model.ai_recommendations ? `
      <div class="ai-section">
        <h2>AI-рекомендации</h2>
        <div class="ai-content">${model.ai_recommendations}</div>
      </div>
    ` : ''}
  `;

  await html2pdf()
    .set({
      margin: [10, 8, 15, 8],
      filename: `${model.model_name} — финмодель.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'landscape',
      },
    })
    .from(container)
    .save();

  document.head.removeChild(style);
}
